import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createColumnHelper } from '@tanstack/react-table';
import { FileDown, Pencil, Search, SlidersHorizontal, Trash2 } from 'lucide-react';
import {
  OrgOnlineAdmissionRow,
  OrgOnlineAdmissionsApi,
  UpdateOrgOnlineAdmissionPayload,
} from '@/services/org-online-admissions.api';
import { OrgPortalApi } from '@/services/org.api';
import { PageHeader } from '@/components/ui/PageHeader';
import { Modal } from '@/components/ui/Modal';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { Field, Input, Select } from '@/components/ui/Input';
import { DataTable, useDataTable } from '@/components/data-table';
import { formatDate } from '@/lib/format';

const columnHelper = createColumnHelper<OrgOnlineAdmissionRow>();

/**
 * Global Applicants List — Org tier. Matches real legacy's own
 * "/online-admissions" listing exactly: hardcoded to pending applications
 * only (no status filter/column exists there — admitted applicants never
 * appear on this listing at all, see OrgOnlineAdmissionsService.list()'s
 * doc comment), columns No./Name/Mobile/WhatsApp/DOB/Class Section/School/
 * Source of Info/Action, an All/Trashed toggle (deleted_at), and a
 * School/Class Section/Session Year/State filter set plus a name/mobile
 * search — no Gender/Place/Father's Name/Applied-date/Status columns
 * (legacy doesn't show them on this listing either, though they're still
 * editable via the Edit modal).
 */
export function OrgOnlineAdmissionsPage() {
  const qc = useQueryClient();
  const [schoolId, setSchoolId] = useState('all');
  const [className, setClassName] = useState('');
  const [sessionYear, setSessionYear] = useState('');
  const [state, setState] = useState('');
  const [showDeleted, setShowDeleted] = useState(false);
  const [editing, setEditing] = useState<OrgOnlineAdmissionRow | null>(null);
  const [deleting, setDeleting] = useState<OrgOnlineAdmissionRow | null>(null);
  const [exporting, setExporting] = useState<'xlsx' | 'pdf' | null>(null);

  const dt = useDataTable({ defaultLimit: 20, defaultSortBy: 'appliedAt', defaultSortOrder: 'desc' });

  useEffect(() => {
    dt.onPaginationChange((prev) => ({ ...prev, pageIndex: 0 }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [schoolId, className, sessionYear, state, showDeleted]);

  const { data: schools = [] } = useQuery({
    queryKey: ['org-schools-for-admissions-filter'],
    queryFn: async () => (await OrgPortalApi.listSchools({ limit: 100 })).items,
  });

  const { data: filters } = useQuery({
    queryKey: ['org-online-admissions-filters'],
    queryFn: OrgOnlineAdmissionsApi.filters,
  });

  const { data, isFetching } = useQuery({
    queryKey: ['org-online-admissions', { ...dt.params, schoolId, className, sessionYear, state, showDeleted }],
    queryFn: () =>
      OrgOnlineAdmissionsApi.list({
        ...dt.params,
        schoolId: schoolId || undefined,
        className: className || undefined,
        sessionYear: sessionYear || undefined,
        state: state || undefined,
        showDeleted: showDeleted || undefined,
      }),
    placeholderData: keepPreviousData,
  });

  const openPdf = async (id: string) => {
    const html = await OrgOnlineAdmissionsApi.pdfHtml(id);
    const w = window.open('', '_blank');
    if (!w) {
      alert('Please allow pop-ups to view the application PDF.');
      return;
    }
    w.document.open();
    w.document.write(html);
    w.document.close();
  };

  const update = useMutation({
    mutationFn: (payload: UpdateOrgOnlineAdmissionPayload) => OrgOnlineAdmissionsApi.update(editing!.id, payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['org-online-admissions'] });
      setEditing(null);
    },
  });

  const remove = useMutation({
    mutationFn: (id: string) => OrgOnlineAdmissionsApi.remove(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['org-online-admissions'] });
      setDeleting(null);
    },
  });

  const runExport = async (format: 'xlsx' | 'pdf') => {
    setExporting(format);
    try {
      await OrgOnlineAdmissionsApi.export(format, {
        schoolId: schoolId || undefined,
        className: className || undefined,
        sessionYear: sessionYear || undefined,
        state: state || undefined,
        search: dt.params.search,
      });
    } finally {
      setExporting(null);
    }
  };

  const columns = [
    columnHelper.display({
      id: 'no',
      header: 'No.',
      enableSorting: false,
      cell: (info) => (
        <span className="text-slate-400">{dt.pagination.pageIndex * dt.pagination.pageSize + info.row.index + 1}</span>
      ),
    }),
    columnHelper.accessor('name', { header: 'Name' }),
    columnHelper.accessor('mobile', { header: 'Mobile', cell: (c) => c.getValue() ?? '—' }),
    columnHelper.accessor('whatsapp', { header: 'WhatsApp', cell: (c) => c.getValue() ?? '—' }),
    columnHelper.accessor('dob', { header: 'Date of Birth', cell: (c) => (c.getValue() ? formatDate(c.getValue()!) : '—') }),
    columnHelper.display({
      id: 'classSection',
      header: 'Class Section',
      cell: (c) => [c.row.original.className, c.row.original.sectionName].filter(Boolean).join(' · ') || '—',
    }),
    columnHelper.accessor('schoolName', { header: 'School' }),
    columnHelper.accessor('sourceOfInfo', { header: 'Source of Info', cell: (c) => c.getValue() ?? '—' }),
    columnHelper.display({
      id: 'actions',
      header: '',
      enableSorting: false,
      cell: (c) => (
        <div className="flex items-center gap-1">
          <button className="rounded-md p-1.5 text-slate-500 hover:bg-slate-100" title="PDF" onClick={() => openPdf(c.row.original.id)}>
            <FileDown className="h-4 w-4" />
          </button>
          <button className="rounded-md p-1.5 text-slate-500 hover:bg-slate-100" title="Edit" onClick={() => setEditing(c.row.original)}>
            <Pencil className="h-4 w-4" />
          </button>
          <button className="rounded-md p-1.5 text-slate-500 hover:bg-red-50 hover:text-red-600" title="Delete" onClick={() => setDeleting(c.row.original)}>
            <Trash2 className="h-4 w-4" />
          </button>
        </div>
      ),
    }),
  ];

  return (
    <>
      <PageHeader
        title="Online Admission — Applicants"
        description="Pending applications across your organization's schools."
        actions={
          <div className="flex items-center gap-2">
            <Link to="/org/online-admission/classes" className="btn-secondary inline-flex items-center gap-1.5">
              <SlidersHorizontal className="h-4 w-4" /> Manage Classes
            </Link>
            <div className="inline-flex overflow-hidden rounded-md border border-slate-200">
              <button
                className="px-2.5 py-1.5 text-sm text-slate-600 hover:bg-slate-50 disabled:opacity-50"
                onClick={() => runExport('xlsx')}
                disabled={!!exporting}
              >
                {exporting === 'xlsx' ? 'Exporting…' : 'Export Excel'}
              </button>
              <div className="w-px bg-slate-200" />
              <button
                className="px-2.5 py-1.5 text-sm text-slate-600 hover:bg-slate-50 disabled:opacity-50"
                onClick={() => runExport('pdf')}
                disabled={!!exporting}
              >
                {exporting === 'pdf' ? 'Exporting…' : 'Export PDF'}
              </button>
            </div>
          </div>
        }
      />

      <div className="mb-3 inline-flex overflow-hidden rounded-md border border-slate-200">
        <button
          className={`px-3 py-1.5 text-sm ${!showDeleted ? 'bg-brand-600 text-white' : 'text-slate-600 hover:bg-slate-50'}`}
          onClick={() => setShowDeleted(false)}
        >
          All
        </button>
        <button
          className={`px-3 py-1.5 text-sm ${showDeleted ? 'bg-brand-600 text-white' : 'text-slate-600 hover:bg-slate-50'}`}
          onClick={() => setShowDeleted(true)}
        >
          Trashed
        </button>
      </div>

      <DataTable
        columns={columns}
        result={data}
        state={dt}
        loading={isFetching}
        emptyMessage="No applications found."
        toolbar={
          <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center">
            <Select value={schoolId} onChange={(e) => setSchoolId(e.target.value)} className="sm:w-56">
              <option value="all">All schools</option>
              {schools.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name}
                </option>
              ))}
            </Select>
            <Select value={className} onChange={(e) => setClassName(e.target.value)} className="sm:w-44">
              <option value="">All classes</option>
              {(filters?.classNames ?? []).map((n) => (
                <option key={n} value={n}>
                  {n}
                </option>
              ))}
            </Select>
            <Select value={sessionYear} onChange={(e) => setSessionYear(e.target.value)} className="sm:w-40">
              <option value="">All session years</option>
              {(filters?.sessionYearNames ?? []).map((n) => (
                <option key={n} value={n}>
                  {n}
                </option>
              ))}
            </Select>
            <Select value={state} onChange={(e) => setState(e.target.value)} className="sm:w-40">
              <option value="">All states</option>
              {(filters?.stateNames ?? []).map((n) => (
                <option key={n} value={n}>
                  {n}
                </option>
              ))}
            </Select>
            <div className="relative sm:w-64">
              <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
              <Input
                value={dt.searchInput}
                onChange={(e) => dt.setSearchInput(e.target.value)}
                placeholder="Search name, mobile"
                className="pl-9"
              />
            </div>
          </div>
        }
      />

      {editing && (
        <EditApplicationModal
          row={editing}
          onClose={() => setEditing(null)}
          onSave={(payload) => update.mutate(payload)}
          saving={update.isPending}
          errorMsg={errMsg(update.error)}
        />
      )}

      <ConfirmDialog
        open={!!deleting}
        onClose={() => setDeleting(null)}
        onConfirm={() => deleting && remove.mutate(deleting.id)}
        loading={remove.isPending}
        title="Delete application?"
        message={`"${deleting?.name}"'s application will be permanently removed. This can't be undone.`}
        confirmText="Delete"
      />
    </>
  );
}

function EditApplicationModal({
  row,
  onClose,
  onSave,
  saving,
  errorMsg,
}: {
  row: OrgOnlineAdmissionRow;
  onClose: () => void;
  onSave: (payload: UpdateOrgOnlineAdmissionPayload) => void;
  saving: boolean;
  errorMsg?: string;
}) {
  const [form, setForm] = useState<UpdateOrgOnlineAdmissionPayload>({
    name: row.name,
    gender: row.gender ?? undefined,
    dob: row.dob ?? undefined,
    mobile: row.mobile ?? undefined,
    whatsapp: row.whatsapp ?? undefined,
    fatherName: row.fatherName ?? undefined,
    place: row.place ?? undefined,
    sourceOfInfo: row.sourceOfInfo ?? undefined,
  });

  return (
    <Modal
      open
      onClose={onClose}
      title="Edit application"
      size="md"
      footer={
        <>
          <button type="button" className="btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button type="button" className="btn-primary" onClick={() => onSave(form)} disabled={saving}>
            {saving ? 'Saving…' : 'Save changes'}
          </button>
        </>
      }
    >
      <div className="space-y-4">
        <Field label="Name">
          <Input value={form.name ?? ''} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} />
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Gender">
            <Select value={form.gender ?? ''} onChange={(e) => setForm((f) => ({ ...f, gender: e.target.value }))}>
              <option value="">—</option>
              <option value="male">Male</option>
              <option value="female">Female</option>
            </Select>
          </Field>
          <Field label="Date of Birth">
            <Input type="date" value={form.dob ?? ''} onChange={(e) => setForm((f) => ({ ...f, dob: e.target.value }))} />
          </Field>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Mobile">
            <Input value={form.mobile ?? ''} onChange={(e) => setForm((f) => ({ ...f, mobile: e.target.value }))} />
          </Field>
          <Field label="WhatsApp">
            <Input value={form.whatsapp ?? ''} onChange={(e) => setForm((f) => ({ ...f, whatsapp: e.target.value }))} />
          </Field>
        </div>
        <Field label="Father's Name">
          <Input value={form.fatherName ?? ''} onChange={(e) => setForm((f) => ({ ...f, fatherName: e.target.value }))} />
        </Field>
        <Field label="Place">
          <Input value={form.place ?? ''} onChange={(e) => setForm((f) => ({ ...f, place: e.target.value }))} />
        </Field>
        <Field label="Source of Information">
          <Input value={form.sourceOfInfo ?? ''} onChange={(e) => setForm((f) => ({ ...f, sourceOfInfo: e.target.value }))} />
        </Field>
        {errorMsg && <div className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">{errorMsg}</div>}
      </div>
    </Modal>
  );
}

function errMsg(e: unknown): string | undefined {
  if (!e) return undefined;
  const anyE = e as any;
  return anyE?.response?.data?.error?.message ?? anyE?.message ?? 'Something went wrong';
}
